import { Lang } from '@axl-dev/school-sdk';
import { App as AntApp, ConfigProvider, message } from 'antd';
import enUS from 'antd/es/locale/en_US';
import esES from 'antd/es/locale/es_ES';
import idID from 'antd/es/locale/id_ID';
import ruRU from 'antd/es/locale/ru_RU';
import ukUA from 'antd/es/locale/uk_UA';
import zhCN from 'antd/es/locale/zh_CN';
import zhTW from 'antd/es/locale/zh_TW';
import dayjs from 'dayjs';
import moment from 'moment';
// #region i18n
import 'moment/dist/locale/es';
import 'moment/dist/locale/id';
import 'moment/dist/locale/ru';
import 'moment/dist/locale/uk';
import 'moment/dist/locale/zh-cn';
import 'moment/dist/locale/zh-tw';
// #endregion
import { FC, useEffect, useMemo } from 'react';

import { Pages } from '@app/pages/index';
import { AppProvider } from '@app/shared/lib/AppProvider';
import { ChatProvider } from '@app/features/chat/ChatProvider';
import { DeviceProvider } from '@app/shared/lib/DeviceProvider';
import { GamificationBalanceProvider } from '@app/shared/lib/GamificationBalanceProvider';
import { useLang } from '@app/shared/lib/hooks';
import { LocProvider } from '@app/shared/lib/localization';
import { SchoolProvider } from '@app/shared/lib/SchoolProvider';
import { SdkProvider } from '@app/shared/lib/SdkProvider';
import '@app/shared/ui/styles/global.scss';
import { UIAppStateProvider } from '@app/shared/ui/UIAppState/UIAppStateProvider';

import StagingBadge from './staging/StagingBadge';

import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/ru';

dayjs.extend(updateLocale);

dayjs.updateLocale('ru', {
    weekStart: 1
});
const AppConfig: React.FC = () => {
    const { lang } = useLang();

    useEffect(() => {
        message.config({ top: 70 });
    }, []);

    useMemo(() => {
        switch (lang) {
            case Lang.zhtw:
                moment.locale('zh-tw');
                break;
            case Lang.zhcn:
                moment.locale('zh-cn');
                break;
            default:
                moment.locale(lang);
                break;
        }
    }, [lang]);

    const antLocale = useMemo(() => {
        switch (lang) {
            case Lang.en:
                return enUS;
            case Lang.id:
                return idID;
            case Lang.ru:
                return ruRU;
            case Lang.uk:
                return ukUA;
            case Lang.es:
                return esES;
            case Lang.zhtw:
                return zhTW;
            case Lang.zhcn:
                return zhCN;
            default:
                break;
        }
    }, [lang]);

    return (
        <ConfigProvider locale={antLocale}>
            <AntApp>
                <Pages />
                <StagingBadge />
            </AntApp>
        </ConfigProvider>
    );
};

export const App: FC = () => (
    <SdkProvider>
        <DeviceProvider>
            <ChatProvider>
                <AppProvider>
                    <SchoolProvider>
                        <LocProvider>
                            <UIAppStateProvider>
                                <GamificationBalanceProvider>
                                    <AppConfig />
                                </GamificationBalanceProvider>
                            </UIAppStateProvider>
                        </LocProvider>
                    </SchoolProvider>
                </AppProvider>
            </ChatProvider>
        </DeviceProvider>
    </SdkProvider>
);
